import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Modal } from '../../layouts/Modal';

import { TextField } from '../base/TextField';
import { Button } from '../base/Button';
import { SectionLoading } from '../shared/SectionLoading';

import { Select } from '../base/Select';
import { Toggle } from '../base/Toggle';
import { Form } from '../base/Form/Form';
import { FormAutoCompleteCombobox } from '../base/Form/FormAutoCompleteCombobox';
import { FormToggle } from '../base/Form/FormToggle';
import {
  ApplianceManufacturerFormFields,
  IApplianceManufacturer,
} from '../../types/Appliance/ApplianceManufacturer';
import useSuperAdminApplianceTypes from '../../data/appliance/useSuperAdminApplianceTypes';
import useApplianceManufacturerMutations from '../../data/appliance/useApplianceManufacturerMutations';

export interface CreateEditApplianceManufacturerProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  applianceManufacturer?: IApplianceManufacturer;
}

export function CreateEditApplianceManufacturer({
  open,
  onClose,
  applianceManufacturer,
}: CreateEditApplianceManufacturerProps) {
  const { superAdminApplianceTypes } = useSuperAdminApplianceTypes();
  const { createApplianceManufacturer, updateApplianceManufacturer } =
    useApplianceManufacturerMutations();

  const handleCreate = (data: ApplianceManufacturerFormFields) => {
    createApplianceManufacturer.mutate(
      { ...data, hardwareTypeIds: data.hardwareTypeIds.map((ht) => ht.value) },
      { onSuccess: () => onClose(false) }
    );
  };

  const handleUpdate = (data: ApplianceManufacturerFormFields) => {
    updateApplianceManufacturer.mutate(
      {
        ...applianceManufacturer,
        ...data,
        hardwareTypeIds: data.hardwareTypeIds.map((ht) => ht.value),
      },
      { onSuccess: () => onClose(false) }
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${
        applianceManufacturer ? 'Edit' : 'Create'
      } Hardware Manufacturer`}
      description={`Use this form to ${
        applianceManufacturer ? 'edit' : 'create'
      } a hardware manufacturer`}
    >
      {superAdminApplianceTypes.isLoading ? (
        <SectionLoading />
      ) : (
        <Form<ApplianceManufacturerFormFields>
          defaultValues={
            applianceManufacturer
              ? {
                  ...applianceManufacturer,
                  hardwareTypeIds: applianceManufacturer.hardwareTypeIds.map(
                    (ht) => ({
                      value: ht,
                      label: superAdminApplianceTypes.data?.find(
                        (t) => t.id === ht
                      )?.name,
                    })
                  ),
                }
              : {}
          }
          onSubmit={applianceManufacturer ? handleUpdate : handleCreate}
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4 overflow-visible"
        >
          {({ register, control }) => (
            <>
              <FormAutoCompleteCombobox
                {...register('hardwareTypeIds')}
                label="Hardware Types"
                options={
                  superAdminApplianceTypes.data?.map((type) => ({
                    value: type.id,
                    label: type.name,
                  })) ?? []
                }
              />
              <TextField {...register('name')} label="Name" />
              <div className="mt-6 flex sm:col-span-2 justify-between">
                <div className="space-x-4">
                  <Button type="submit">
                    {applianceManufacturer ? 'Update' : 'Create'}
                  </Button>
                  <Button variant="outlined" onClick={() => onClose(false)}>
                    Cancel
                  </Button>
                </div>
                <FormToggle
                  toggleProps={{ label: 'Disabled' }}
                  control={control}
                  {...register('disabled')}
                />
              </div>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
}
